/**
 * GNSS integrity alarm runner: poor accuracy, lost fix, and a sudden position
 * jump (possible spoofing). It gathers the inputs the pure evaluator needs from
 * the stores, works out which are firing, and merges the result into the live
 * alarm state — the same place the siren, haptics and banner already read from,
 * so these ring exactly like the other alarms.
 *
 * It ticks once a second as well as on store changes, because two of the inputs
 * grow with wall-clock time rather than a store write: the fix age (lost fix) and
 * the window after a teleport (the jump alarm clears itself a few seconds later).
 * All three are judged on the real device feed only; on the simulator the
 * evaluator returns null for everything, so the training position never trips
 * them.
 *
 * It preserves the rest of the live snapshot on every write (reading it back from
 * the store first), the same co-operative pattern the AIS alarms use, so it never
 * clobbers an anchor drag or a depth alarm computed elsewhere.
 */
import { useEffect } from 'react';
import { useAlarmsStore } from '@/store/alarms';
import { useGnssStore } from '@/store/gnss';
import { usePositionSourceStore } from '@/store/positionSource';
import { useChartPlotterStore } from '@/store';
import { evaluateGnssAlarms, GNSS_JUMP_WINDOW_MS } from '@/gnss';

export function GnssAlarmsRunner() {
  const sourceKind = usePositionSourceStore((s) => s.sourceKind);
  const accuracyEnabled = useAlarmsStore((s) => s.config.gnssAccuracyEnabled);
  const accuracyM = useAlarmsStore((s) => s.config.gnssAccuracyM);
  const lostEnabled = useAlarmsStore((s) => s.config.gnssLostEnabled);
  const jumpEnabled = useAlarmsStore((s) => s.config.gnssJumpEnabled);
  // Health + accuracy + teleport move under us; subscribe so an edge is caught
  // immediately, and tick every second for the time-based ones.
  const fixHealth = usePositionSourceStore((s) => s.fixHealth);
  const fixAgeSec = usePositionSourceStore((s) => s.fixAgeSec);
  const ownAccuracyM = useChartPlotterStore((s) => s.ownship.accuracyM);
  const lastTeleportMs = useGnssStore((s) => s.lastTeleportMs);

  useEffect(() => {
    const compute = () => {
      const res = evaluateGnssAlarms({
        onDevice: usePositionSourceStore.getState().sourceKind === 'device',
        accuracyM: useChartPlotterStore.getState().ownship.accuracyM ?? null,
        accuracyEnabled: useAlarmsStore.getState().config.gnssAccuracyEnabled,
        accuracyLimitM: useAlarmsStore.getState().config.gnssAccuracyM,
        lostEnabled: useAlarmsStore.getState().config.gnssLostEnabled,
        fixHealth: usePositionSourceStore.getState().fixHealth,
        fixAgeSec: usePositionSourceStore.getState().fixAgeSec,
        jumpEnabled: useAlarmsStore.getState().config.gnssJumpEnabled,
        lastTeleportMs: useGnssStore.getState().lastTeleportMs,
        nowMs: Date.now(),
        jumpWindowMs: GNSS_JUMP_WINDOW_MS,
      });
      const live = useAlarmsStore.getState().live;
      // Only write when a field actually changed, so we don't churn the store
      // (and every subscriber) once a second for no reason.
      const same =
        JSON.stringify(live.gnssAccuracy) === JSON.stringify(res.gnssAccuracy) &&
        JSON.stringify(live.gnssLost) === JSON.stringify(res.gnssLost) &&
        JSON.stringify(live.gnssJump) === JSON.stringify(res.gnssJump);
      if (same) return;
      useAlarmsStore.getState().setLive({ ...live, ...res });
    };

    compute();
    const id = window.setInterval(compute, 1000);
    return () => window.clearInterval(id);
  }, [sourceKind, accuracyEnabled, accuracyM, lostEnabled, jumpEnabled, fixHealth, fixAgeSec, ownAccuracyM, lastTeleportMs]);

  return null;
}
